import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  // TS - Custom override: whether the last trigger activation was pointer-driven.
  /** @type {boolean} */
  #pointerOpen = false;

  // TS - Custom override: last-input modality — the add-to-cart auto-open has
  // no trigger event to inspect, so track it at the document level.
  /** @type {boolean} */
  #lastInputWasPointer = false;

  // TS - Custom override: modality of the in-flight open. The cart morph syncs
  // the dialog's attributes to server HTML between showDialog() and showModal(),
  // stripping data-pointer-open — this field survives it.
  /** @type {boolean} */
  #openWasPointer = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // TS - Custom override: keyboard input while open restores the focus ring
    // that a pointer-driven open suppresses (see showDialog). Document-level
    // because modality is also tracked while the drawer is closed (auto-open).
    document.addEventListener('keydown', this.#clearPointerOpen);
    document.addEventListener('pointerdown', this.#trackPointerInput, { passive: true });
    // TS - Custom override: keep the ring suppression across the cart morph.
    this.addEventListener(DialogOpenEvent.eventName, this.#restampPointerOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('keydown', this.#clearPointerOpen);
    document.removeEventListener('pointerdown', this.#trackPointerInput);
    this.removeEventListener(DialogOpenEvent.eventName, this.#restampPointerOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  // TS - Custom override
  // Push the drawer's history entry BEFORE DialogComponent scroll-locks
  // <body> (upstream pushed on DialogOpenEvent, after the lock). The lock
  // zeroes window.scrollY, and the browser snapshots the current entry's
  // scroll offset at pushState time — so the history.back() that closes the
  // drawer restored that 0 offset and yanked the page to the top on mobile.
  // Pushing pre-lock stores the real offset, so the browser's own restoration
  // lands where the user left off.
  showDialog() {
    if (!this.refs.dialog.open) {
      // TS - Custom override: stamp pointer-driven opens so the stylesheet can
      // drop the ring WebKit paints on showModal()'s programmatic focus of the
      // close button (taps never focus buttons in WebKit, so the previous
      // focus-visible state carries over). Keyboard opens keep the ring.
      this.refs.dialog.toggleAttribute('data-pointer-open', this.#pointerOpen);
      this.#openWasPointer = this.#pointerOpen;
      this.#pointerOpen = false;
      this.#handleHistoryOpen();
    }
    super.showDialog();
  }

  // TS - Custom override
  #clearPointerOpen = () => {
    this.#lastInputWasPointer = false;
    this.refs.dialog?.removeAttribute('data-pointer-open');
  };

  // TS - Custom override
  #trackPointerInput = () => {
    this.#lastInputWasPointer = true;
  };

  // TS - Custom override: on pointer-driven opens, re-stamp data-pointer-open
  // after the cart morph (which syncs attributes to server HTML and strips it).
  // The stylesheet uses it to drop the ring iOS paints on showModal()'s
  // programmatic focus; focus itself stays on the close button (dialog pattern).
  #restampPointerOpen = () => {
    if (!this.#openWasPointer) return;
    this.refs.dialog?.setAttribute('data-pointer-open', 'true');
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    // TS - Custom override
    this.#historyAbortController?.abort();
    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    // TS - Custom override: a failed add (e.g. Shopify rejecting the gift-card
    // recipient fields) also dispatches CartAddEvent, flagged with didError.
    // The error message renders at the buy buttons — don't pop the drawer
    // over it when nothing was added.
    if (event.detail.data?.didError) return;

    if (this.hasAttribute('auto-open')) {
      // TS - Custom override: inherit the add action's input modality so a
      // tapped add-to-cart doesn't focus/ring the X when the drawer pops.
      this.#pointerOpen = this.#lastInputWasPointer;
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /** @param {Event} [event] */
  open(event) {
    // TS - Custom override: keyboard-activated clicks carry an empty
    // pointerType — only real taps/clicks count as pointer.
    this.#pointerOpen = event instanceof PointerEvent && event.pointerType !== '';
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
